import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store, select } from '@ngrx/store';
import { cloneDeep, filter } from 'lodash-es';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, skipWhile, take, takeUntil, tap } from 'rxjs/operators';
import { selectAllMembers, selectAllTicketsTags } from 'src/app/settings/store/settings.selectors';
import { AppState } from '../../../core/core.state';
import { Property, SavedReply, Tag, User, Visitor } from '../../../models';
import { TicketsService } from '../../services/tickets.service';
import * as TicketsActions from '../../store/tickets.actions';
import { selectReplySending, selectTicketById, selectTicketDeleteLoading, selectTicketSaveLoading } from '../../store/tickets.selectors';
import { Ticket, TicketStatus, TicketsViewMode } from '../../models/ticket.model';
import { TicketReply } from '../../models/ticketreply.model';
import { Update } from '@ngrx/entity';
import { currentUser } from '../../../core/auth';
import { selectPropertyById } from '../../../core/store/properties.selectors';
import { HttpEventType } from '@angular/common/http';
import * as ToastrActions from '../../../core/store/toastr.actions';
import * as VisitorActions from '../../store/visitors.actions';
import { selectAllSavedReplies } from '../../../core/store/saved.replies.selectors';
import { PendingAttachment } from '../../../models/attachment.model';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { RocketEditComponent } from 'src/app/core/rocket-edit/rocket-edit.component';
import { CommonModule } from '@angular/common';
import { CharlaTicketContentComponent } from './ticket.content.component';
import { TagInputModule } from 'ngx-chips';
import { CharlaPriorityComponent } from '../ticket-priority/priority.component';
import { CharlaTicketStatusComponent } from '../ticket-status/status.component';
import { CharlaAvatarComponent } from '../../../core/avatar/avatar.component';
import { DateFormatPipe, FromUtcPipe, LocalTimePipe } from '../../../core/pipes/dates.pipe';
import { CharlaAssigneeComponent } from '../ticket-assignee/assignee.component';
import { CharlaPropertyLabelComponent } from '../../../core/property-label/property-label.component';
import { AttachmentComponent } from '../../../core/attachment/attachment.component';
import { ContactEditComponent } from '../../../core/contact-edit/contact-edit.component';
import { FormatCcPipe } from '../../../core/pipes/formatcc.pipe';
import { TimeAgoPipe } from '../../../core/pipes/timeago.pipe';
import { TagEditorComponent } from '../../../core/tag-editor/tag-editor.component';
import { NgbDropdownModule, NgbPopover, NgbPopoverModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TicketSourcePipe } from '../../pipes/compactstring';
import { LiveTranslationComponent } from 'src/app/core/live-translation/live-translation.component';
import { InlineSVGDirective } from 'src/app/core/inline-svg/inline-svg.directive';
import { HasRoleDirective } from 'src/app/core/hasrole.directive';


@Component({
    selector: 'charla-ticket',
    templateUrl: './ticket.component.html',
    styleUrls: ['./ticket.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, EditorModule, TagInputModule, ReactiveFormsModule, RouterModule, NgbPopoverModule, NgbTooltipModule, NgbDropdownModule,
		RocketEditComponent, CharlaPriorityComponent, CharlaTicketStatusComponent, CharlaAvatarComponent,
		LocalTimePipe, CharlaAssigneeComponent, CharlaPropertyLabelComponent, AttachmentComponent, CharlaTicketContentComponent,
		ContactEditComponent, FormatCcPipe, FromUtcPipe, TimeAgoPipe, DateFormatPipe, TagEditorComponent, TicketSourcePipe,
		LiveTranslationComponent, InlineSVGDirective, HasRoleDirective
	],
	providers: [
		{ provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' }
	]
})
export class TicketComponent{

	@Input() ticket: Ticket;
	@Input() showSidebar: boolean = true;
	@Input() isInlineView: boolean = false;

	save_loading$: Observable<boolean>;
	delete_loading$: Observable<boolean>;
	sending$: Observable<boolean>;
	upload_inprogress$: Observable<boolean>;
	pending_attachments: PendingAttachment[] = [];
	shortcuts: SavedReply[];
	TicketStatus = TicketStatus;

	//ticket: Ticket;
	tags$: Observable<Tag[]>;
	highlight: number = 0;
	form_reply: UntypedFormGroup;
    unsubscribe$ = new Subject();
	users$: Observable<User[]>;
	agents$: Observable<User[]>;

	editor_ref: any;
	content_translated: string = "";
	@ViewChild('popoverTranslate') popoverTranslate!: NgbPopover;
	is_live_translation: boolean = false;
	language_agent: string = "";
	language_visitor: string = "";

	//property: Property;
	tiny_config: any;
	current_user$: Observable<User>;
	groups =  [
		{	
			title: "Admins",
			role: "",
			email:"",
			type: "Groups",
			id: 'admins'
		},		
		{	
			title: "Everyone",
			role: "",
			email:"",
			type: "Groups",
			id: 'everyone'
		}
	];

	constructor( 
		private store: Store<AppState>,
        private fb: UntypedFormBuilder,
		private actions$: ActionsSubject,
		private cd: ChangeDetectorRef,
		private ticketService: TicketsService
	) {
        
		this.form_reply = this.fb.nonNullable.group({
			content: ['', Validators.compose([
				Validators.required,
				Validators.maxLength(255)
			])],
			is_private: false,
			cc: null
		});

		this.store.select(selectAllSavedReplies).pipe(
			takeUntil(this.unsubscribe$)
		).subscribe(sr => {
			this.shortcuts = sr
		});

		this.save_loading$ = this.store.select(selectTicketSaveLoading);
		this.delete_loading$ = this.store.select(selectTicketDeleteLoading);
		this.sending$ = this.store.select(selectReplySending);
		//this.upload_inprogress$ = this.store.select(selectArticleUploadLoading);
		
		// this needs to include all the history of all users
		// @todo settings controller should not be returning all members event the deleted ones
		this.agents$ = this.store.pipe(select(selectAllMembers));

		this.tags$ = this.store.select(selectAllTicketsTags);
		this.current_user$ = this.store.pipe(select(currentUser));

		// this needs to be filtered without deleted users
		this.users$ = this.store.pipe(
			select(selectAllMembers),
			takeUntil(this.unsubscribe$),
			map( uos => uos.filter(uo => !uo.is_deleted)),
			map(m => {
				let u  = cloneDeep(m);
				u.forEach(function (user:any) {
					user.title = `${user.fullname}`;
					user.type = "Users"
				});
				u.unshift.apply(u, this.groups );
				return u;
			})
		);

		/*this.actions$.pipe(
			ofType(TicketsActions.TicketSaveSuccess),
			takeUntil(this.unsubscribe$)
		).subscribe(message => { 
				this.show_save_success = true;
				setTimeout(() => {
					this.show_save_success = false;
					this.cd.detectChanges();
				}, 5000);
		});*/

		this.actions$.pipe(
			ofType(TicketsActions.ReplySaveSuccess),
			takeUntil(this.unsubscribe$)
		).subscribe(reply => { 
			this.form_reply.reset();
			this.highlight = reply.reply.id;
				setTimeout(() => {
					this.highlight = 0;
					this.cd.detectChanges();
				}, 5000);
		});
	
		this.actions$.pipe(
			ofType(TicketsActions.TicketDeleteSuccess),
			takeUntil(this.unsubscribe$)
		).subscribe(message => { 
			//this.onBack.emit();
		});

		var that = this;
		this.tiny_config = {
			min_height: 100,
			height: 100,
			menubar: false,
			statusbar: false,
			skin: 'naked',
			icons: 'small',
			base_url: '/tinymce', 
			suffix: '.min'	,
			toolbar_location: 'bottom',
			setup: (editor) => {

				this.editor_ref = editor;

				editor.addShortcut('ctrl+13', 'Submit Form', () => {
				  this.save_reply();
				});

				var onAction = function (autocompleteApi, rng, value) {
					editor.selection.setRng(rng);
					editor.insertContent(value);
					autocompleteApi.hide();
				  };

				  var getMatchedChars = function (pattern) {
					return that.shortcuts.filter(function (char) {
					  return char.shortcut.startsWith(pattern) ;
					});
				  };

				editor.ui.registry.addAutocompleter('shortcuts', {
					ch: '/',
					minChars: 0,
					columns: 1,
					onAction: onAction,
					fetch: function (pattern) {
					  return new Promise(function (resolve) {
						var results = getMatchedChars(pattern).map(function (char) {
						  return {
							type: 'autocompleteitem',
							value: char.reply,
							text: char.shortcut
						  }
						});
						resolve(results);
					  });
					}
				});


				editor.ui.registry.addToggleButton('languagebutton', {
					icon: 'language',
					tooltip: 'Enable Live Translation',
					onAction: (api) => {
						// We'll trigger the Angular popover here
						const event = new CustomEvent('openLiveTranslationPopover', { bubbles: true });
						document.dispatchEvent(event);
					},
					onSetup(api) {
						// subscribe to an editor-level event so external code can toggle this button
						const handler = (e) => {
							const active = !!e.active;
							api.setActive(active);
							//handleLanguageToggle(editor, active); // optional: replicate side-effects
						};
						editor.on('ToggleLiveTranslationButton', handler);

						// cleanup function called when control is destroyed
						return () => editor.off('ToggleLiveTranslationButton', handler);
				}
				});

				editor.ui.registry.addToggleButton('refreshlanguagebutton', {
					icon: 'reload',
					tooltip: 'Refresh Translation',
					onAction: (api) => {
						// We'll trigger the Angular popover here
						this.updateTranslation();
					}
				});
			},
		}

	}

	onEditorReady(event: any) {
	
		document.addEventListener('openLiveTranslationPopover', () => {
			this.popoverTranslate?.open();
		});
	
		this.editor_ref.fire('ToggleLiveTranslationButton', { active: this.ticket.recipient?.is_live_translation });
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['ticket'] && this.ticket){

			this.is_live_translation = this.ticket.recipient?.is_live_translation;
			this.language_agent = this.ticket.recipient?.language_agent;
			this.language_visitor = this.ticket.recipient?.language_visitor;

			// in case the ticket is changed externally we need to update the button state
			this.editor_ref?.fire('ToggleLiveTranslationButton', { active: this.ticket.recipient?.is_live_translation });
			this.content_translated = "";

		}
	};

	save_assignees(values: User[]){
        
        var entity_update: Update<Ticket> = {
            id: this.ticket.id,
            changes: { assignees: values }
        };

        this.store.dispatch( TicketsActions.AssigneeSaveRequest( {ticket: entity_update}));	
    }

	save_priority(priority: number){
        
		if (priority == this.ticket.priority) return;

		var entity_update: Update<Ticket> = {
            id: this.ticket.id,
            changes: { priority: priority }
        };
		this.store.dispatch( TicketsActions.PrioritySaveRequest( {ticket: entity_update}));	

	}

	save_tags(values: Tag[]){

        var entity_update: Update<Ticket> = {
            id: this.ticket.id,
            changes: { tags: values }
        };

		this.store.dispatch( TicketsActions.TagsSaveRequest( {ticket: entity_update}));	

	}

	delete_ticket(){
		this.store.dispatch(TicketsActions.TicketDeleteRequest({ticket_id: this.ticket.id}));
	}

	update_status(status: number){

        var entity_update: Update<Ticket> = {
            id: this.ticket.id,
            changes: { status: status }
        };
		this.store.dispatch(TicketsActions.StatusSaveRequest({ticket: entity_update, updateView: !this.isInlineView	}));
	}
	
	save_reply(status: number = TicketStatus.PendingCustomer){

		// Check for still in progress attachment 
		if (this.pending_attachments.filter( q => q.id == "" ).length > 0 ){
			this.store.dispatch(ToastrActions.showToastrError({message: "You attachment is still pending upload. Please wait or cancel upload and retry.", title: "Somethig went wrong"}));
			return;
		}

		var reply: TicketReply = {
			...this.form_reply.value,
			ticket_id: this.ticket.id,
			content_translated: this.content_translated,
			status: status
		};

		// add attachment
		//todo check for still uploading attachments
		if (this.pending_attachments && this.pending_attachments.length > 0){
			reply.attachments = this.pending_attachments.map(q => q.id);
		} 

		this.store.dispatch(TicketsActions.ReplySaveRequest({reply: reply, updateView: !this.isInlineView}));

		this.pending_attachments = [];

	}

	update_cc(value){

		console.log(value);
		this.form_reply.patchValue({ cc: value});

	}

    addTagFn(name){
		return { name: name, tag: true };
	}

    onFileSelected(event) {

        const files : FileList  = event.target.files;

		for (let i = 0; i < files.length; i++) {
			const file = files[i];

			if (file) {
				//this.pending_attachment = 'attaching-file';
	
				let pending = new PendingAttachment();
				pending.filename = file.name;

				this.pending_attachments.push(pending);

				const formData = new FormData();
				formData.append("file", file);
				//this.upload_filename = file.name;
	
			   pending.upload$ = this.ticketService.uploadAttachment(formData).subscribe(
				event => {
					if (event.type == HttpEventType.UploadProgress) {
						pending.upload_progress = Math.round(100 * (event.loaded / event.total));
						this.cd.detectChanges();
					} else if (event.type == HttpEventType.Response){
						pending.id = event.body.model.attachment;
					}
				},
				erro => {
					this.store.dispatch(ToastrActions.showToastrError({message: "There was a problem uploading your attachment. Please retry uploading the file again.", title: "Somethig went wrong"}));
					this.pending_attachments = this.pending_attachments.filter(q => q != pending);
				});
	
			}
		}
    }

	cancel_upload(attachment: PendingAttachment){

		attachment.upload$.unsubscribe();
		this.pending_attachments = this.pending_attachments.filter(q => q != attachment);

		this.cd.detectChanges();
	
	}


	applyLiveTranslation(event: {language_agent: string, language_visitor: string}) {

		if (!this.ticket.recipient) return;
		this.editor_ref.fire('ToggleLiveTranslationButton', { active: true });

		const visitor_update: Update<Visitor> = {
			id: this.ticket.recipient.id,
			changes: { 
				language_agent: event.language_agent,
				language_visitor: event.language_visitor,
				is_live_translation: true 
			}
		};
		this.store.dispatch( VisitorActions.VisitorInfoSaveRequest( {visitor: visitor_update } ));
		
		this.is_live_translation = true;
		this.language_agent = event.language_agent;
		this.language_visitor = event.language_visitor;
		this.updateTranslation();

	}

	stopLiveTranslation() {
	
		this.editor_ref.fire('ToggleLiveTranslationButton', { active: false });

		const visitor_update: Update<Visitor> = {
			id: this.ticket.recipient.id,
			changes: { 
				is_live_translation: false 
			}
		};
		this.store.dispatch( VisitorActions.VisitorInfoSaveRequest( {visitor: visitor_update } ));	
		
		this.content_translated = "";
		this.is_live_translation = false;

	}
	
	updateTranslation() {

		if (!this.is_live_translation) return;

		this.ticketService.translate(this.form_reply.get('content').value,
									this.language_agent ,
									this.language_visitor )
		.subscribe( res => {

			this.content_translated = res.model.content;
			this.cd.detectChanges();

		});

	}

	translateReply(reply_id: number, content: string) {

		this.store.pipe(select(currentUser), take(1)).subscribe(u => {
			this.store.dispatch( TicketsActions.TranslateReplyRequest( {
				reply_id: reply_id,
				ticket_id: this.ticket.id,
				content: content,
				language_agent: u.live_translation_language
			} ) );
		});

	}

    ngOnDestroy() {
		document.removeEventListener('openLiveTranslationPopover', () => {});
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
	
}


